import { ICON } from '@core/util/icon';
import { BusinessSettingsVisibleTemplates } from '@models/documents';
import { Selector } from '@ngxs/store';
import { UrlService } from '@services/url/url.service';
import { AppState } from '@state/app/app.state';
import { BusinessFeatureFlagSelectors, BusinessState } from '@state/business';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { UserSelectors } from '@state/global/user';
import { cloneDeep } from 'lodash';

enum PageTypes {
	integrations = 'integrations',
	businessSettings = 'businessSettings',
	businessDetails = 'businessDetails',
	businessDefaults = 'businessDefaults',
	documentStyling = 'documentStyling',
	documentFormatting = 'documentFormatting',
	security = 'security',
	documentTemplates = 'documentTemplates',
	docxTemplates = 'docxTemplates',
	requisitionTemplates = 'requisitionTemplates',
	invites = 'invites',
	users = 'users',
	trustAccounts = 'trustAccounts',
	checklist = 'checklist',
	billing = 'billing',
	requests = 'requests',
	emailTemplates = 'emailTemplates',
}

const DefaultNavigation: NavigationSections = {
	Teams: {
		icon: ICON.USERS,
		title: 'Team',
		links: {
			[PageTypes.users]: {
				title: 'Users',
				url: UrlService.Manage_Users(),
				order: 0,
				dataIntercomTarget: 'business-users',
			},
			[PageTypes.invites]: {
				title: 'Invites',
				url: UrlService.Manage_Invites(),
				order: 1,
				dataIntercomTarget: 'business-invites',
			},
		},
		order: 0,
	},
	Business: {
		icon: ICON.BUSINESS,
		title: 'Business',
		links: {
			[PageTypes.businessDetails]: {
				title: 'Offices',
				url: UrlService.Manage_Business_Details(),
				order: 0,
				dataIntercomTarget: 'business-details',
			},
			[PageTypes.businessSettings]: {
				title: 'Business Settings',
				url: UrlService.Manage_Finances_General(),
				order: 1,
				dataIntercomTarget: 'business-finances',
			},
			[PageTypes.businessDefaults]: {
				title: 'Matter Defaults',
				url: UrlService.Manage_Business_Defaults(),
				order: 2,
				dataIntercomTarget: 'business-defaults',
			},
			[PageTypes.integrations]: {
				title: 'Integrations',
				url: UrlService.Manage_Integrations(),
				order: 3,
				dataIntercomTarget: 'profile-integration',
			},
			[PageTypes.security]: {
				title: 'Security',
				url: UrlService.Manage_Business_Security(),
				order: 4,
				dataIntercomTarget: 'business-security',
			},
			[PageTypes.billing]: {
				title: 'Billing',
				url: UrlService.Manage_Business_Billing(),
				order: 5,
				dataIntercomTarget: 'business-billing',
			},
			[PageTypes.trustAccounts]: {
				title: 'Funding Methods',
				url: UrlService.Manage_Trust_Accounts(),
				order: 6,
				dataIntercomTarget: 'business-trust-accounts',
			},
			[PageTypes.checklist]: {
				title: 'Checklists',
				url: UrlService.Manage_Checklist(),
				order: 7,
				dataIntercomTarget: 'business-checklists',
			},
		},
		order: 1,
	},
	ClientEngagement: {
		icon: ICON.ENVELOPE,
		title: 'Client Engagement',
		links: {
			[PageTypes.emailTemplates]: {
				title: 'Email Templates',
				url: UrlService.Manage_Business_Email_Templates(),
				order: 0,
				dataIntercomTarget: 'client-engagement-email-templates',
			},
		},
		order: 3,
	},
	Documents: {
		icon: ICON.DOCUMENT,
		title: 'Documents',
		links: {
			[PageTypes.requests]: {
				title: 'Requests',
				url: UrlService.Manage_Business_Requests(),
				order: 0,
				dataIntercomTarget: 'business-requests',
			},
			[PageTypes.documentTemplates]: {
				title: 'Templates',
				url: UrlService.Manage_Business_Template_Documents(),
				order: 0,
				dataIntercomTarget: 'business-templates',
			},
			[PageTypes.docxTemplates]: {
				title: 'Docx Templates',
				url: UrlService.Manage_Business_Docx_Templates(),
				order: 1,
				dataIntercomTarget: 'business-docx-templates',
			},
			[PageTypes.documentStyling]: {
				title: 'Styling',
				url: UrlService.Manage_Document_Styling(),
				order: 1,
				dataIntercomTarget: 'business-document-styling',
			},
			[PageTypes.documentFormatting]: {
				title: 'Formatting',
				url: UrlService.Manage_Document_Formatting(),
				order: 2,
				dataIntercomTarget: 'business-document-formatting',
			},
			[PageTypes.requisitionTemplates]: {
				title: 'Requisitions',
				url: UrlService.Manage_Business_Template_Requisitions(),
				order: 3,
				dataIntercomTarget: 'business-templates-requisitions',
			},
		},
		order: 2,
	},
};

export interface NavigationSections {
	Teams: Navigation;
	Business: Navigation;
	ClientEngagement: Navigation;
	Documents: Navigation;
}

interface Navigation {
	icon: ICON;
	title: string;
	links: { [id: string]: Link };
	order: number;
}

export interface Link {
	title: string;
	url: string;
	dataIntercomTarget: string;
	order: number;
}

export class ProfileSelector {
	@Selector([
		BusinessUsersState.isLoggedInUserAdmin,
		AppState.loadedFromLeap,
		BusinessState.getVisibleTemplates,
		BusinessState.isCollege,
		UserSelectors.canUserViewRequests,
		BusinessFeatureFlagSelectors.isEmailTemplatesEnabled,
	])
	static getNavigation(
		isAdmin: boolean,
		loadedFromLeap: boolean,
		visibleTemplates: BusinessSettingsVisibleTemplates,
		isCollege: boolean,
		canUserViewRequests: boolean,
		isEmailTemplatesEnabled: boolean,
	): NavigationSections {
		const _nav = cloneDeep(DefaultNavigation);

		// ToDo remove this when the feature is ready
		// only use to make menu available for testing LEAP

		if (!isAdmin) {
			delete _nav.Teams;
			delete _nav.ClientEngagement;
			delete _nav.Documents?.links?.[PageTypes.documentTemplates];
			delete _nav.Documents?.links?.[PageTypes.documentStyling];
			delete _nav.Documents?.links?.[PageTypes.documentFormatting];
			delete _nav.Documents?.links?.[PageTypes.requisitionTemplates];
			delete _nav.Documents?.links?.[PageTypes.docxTemplates];
			delete _nav.Business.links?.[PageTypes.trustAccounts];
			delete _nav.Business.links?.[PageTypes.checklist];
			delete _nav.Business.links?.[PageTypes.billing];
			delete _nav.Business.links?.[PageTypes.businessDefaults];
			delete _nav.Business.links?.[PageTypes.businessDetails];
			delete _nav.Business.links?.[PageTypes.security];
			delete _nav.Business.links?.[PageTypes.businessSettings];
		}

		if (isCollege) {
			delete _nav.Business.links?.[PageTypes.billing];
		}

		if (loadedFromLeap) {
			delete _nav.Teams?.links?.[PageTypes.invites];
			delete _nav.Business?.links?.[PageTypes.security];
		}

		if (visibleTemplates === BusinessSettingsVisibleTemplates.WORD) {
			delete _nav.Documents?.links?.[PageTypes.documentTemplates];
			delete _nav.Documents?.links?.[PageTypes.documentStyling];
		}

		if (visibleTemplates === BusinessSettingsVisibleTemplates.HTML) {
			delete _nav.Documents?.links?.[PageTypes.docxTemplates];
		}

		if (!canUserViewRequests) {
			delete _nav.Documents?.links?.[PageTypes.requests];
		}

		if (!isEmailTemplatesEnabled) {
			delete _nav.ClientEngagement;
		}

		return _nav;
	}
}
